import React from 'react';
import PropTypes from 'prop-types';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import WelcomeParagraphs from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/WelcomeParagraphs';
import InfoBox from 'Views/Onboarding/Components/InfoBox';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import AccordionSummary from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccordionDetails from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/AccordionDetails';
import Accordion from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Accordion';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import PageContentTop from 'Components/ComponentLibrary/Molecules/Page/PageContentTop';
import PageContentContainer from 'Components/ComponentLibrary/Molecules/Page/PageContentContainer';

const styles = theme => ({
  content: {
    width: '100%',
  },
  inlineText: {
    display: 'inline',
    lineHeight: 1.5,
  },
  infoBox: {
    paddingTop: theme.spacing(3),
    lineHeight: 1.5,
  },
  accordion: {
    backgroundColor: theme.palette.facet.accentBlue21,
    padding: theme.spacing(2),
    borderRadius: theme.radius.md,
    position: 'initial',
    marginTop: theme.spacing(4),
    '&.Mui-expanded': {
      marginTop: theme.spacing(4),
    },
  },
  roundedAccordion: {
    borderRadius: theme.radius.md,
  },
  accordionDetails: {
    flexDirection: 'column',
  },
  textAlignLeft: {
    textAlign: 'left',
    lineHeight: 1.5,
  },
  listItem: {
    display: 'list-item',
    marginLeft: theme.spacing(3),
    textAlign: 'left',
    marginBottom: theme.spacing(1),
    lineHeight: 1.5,
  },
  welcomeParagraphs: {
    paddingBottom: theme.spacing(0),
  },
  middleParagraph: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  label: {
    marginTop: theme.spacing(3),
    alignItems: 'start',
  },
  esgLabel: {
    alignItems: 'start',
  },
  checkbox: {
    padding: theme.spacing(0, 1),
  },
  link: {
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      color: theme.palette.facet.primaryBlue,
    },
  },
});

const Content = ({
  classes,
}) => {
  const {
    resource,
    handleChange,
  } = useMetaLayoutContext();

  const isEsg = resource.attr('isEsg') === 'true';

  let clientNames = `${resource.attr('primaryApplicantFirstName')} ${resource.attr('primaryApplicantLastName')}`;
  let weOrI = 'I';
  if (resource.attr('secondaryApplicantFirstName') !== undefined) {
    weOrI = 'We';
    clientNames = `${clientNames} and ${resource.attr('secondaryApplicantFirstName')} ${resource.attr('secondaryApplicantLastName')}`;
  }

  return (
    <PageContentContainer>
      <PageContentTop
        title={resource.attr('title')}
        subtitle={resource.attr('subtitle')}
      />
      <Box className={classes.content}>
        <InfoBox
          className={classes.infoBox}
        >
          <p>
            <Text
              className={classes.textAlignLeft}
              color="primaryGray"
              variant="h5"
              weight="medium"
              text="I authorize Facet to be my sole Financial Advisor and act as my agent and on my behalf for this investment account."
            />
          </p>
          <p className="middleParagraph">
            <Text
              className={classes.inlineText}
              color="primaryGray"
              variant="h5"
              weight="medium"
              text="Facet publicly maintains all of it's disclosures and terms of service information at "
            />
            <a href={resource.relationship('legalDocuments').link('related').url()} rel="noopener noreferrer" target="_blank">
              <Text className={classes.inlineText} color="none" variant="h5" weight="medium" text="facetwealth.com/legal-documents" />
            </a>
            <Text className={classes.inlineText} color="primaryGray" variant="h5" weight="medium" text="." />
          </p>
          <p>
            <Text
              className={classes.textAlignLeft}
              color="primaryGray"
              variant="h5"
              weight="medium"
              text="We will communicate any material changes to the link above for all disclosure information."
            />
          </p>
        </InfoBox>
        {
          isEsg ? (
            <Accordion
              classes={{
                root: classes.accordion,
                rounded: classes.roundedAccordion,
              }}
              elevation={0}
              square
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color={'primary'} />}
                aria-controls={'fine-print-content'}
              >
                <Text className={classes.textAlignLeft} variant="h4" weight="medium" text="ESG Disclosure" />
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Text
                  className={classes.textAlignLeft}
                  color="primaryGray"
                  variant="h5"
                  weight="medium"
                  text={resource.attr('additionalDetailTitle')}
                />
                {
                    Array.isArray(resource.attr('additionalDetailItems')) ? (
                      <WelcomeParagraphs
                        className={classes.welcomeParagraphs}
                        paragraphs={resource.attr('additionalDetailItems').map(text => ({ text }))}
                        variant="h5"
                        classes={{ text: classes.listItem }}
                      />
                    ) : resource.attr('subtitle')
                  }
              </AccordionDetails>
            </Accordion>
          ) : undefined
        }
        <FormControlLabel
          className={classes.label}
          control={
            <Checkbox
              className={classes.checkbox}
              checked={resource.attr('primaryApplicantSignature') === 'true'}
              onChange={(e) => handleChange('primaryApplicantSignature', e.target.checked.toString())}
              name="primaryApplicantSignature"
              test="primary-applicant-signature-checkbox"
            />
          }
          label={
            <Text className={classes.textAlignLeft} color="primaryGray" variant="h5" weight="medium">
              {weOrI}, {clientNames}, acknowledge that I have received, reviewed, and agree to Facet&#39;s and Apex (our brokerage clearing firm) <a href={resource.relationship('apexDisclosures').link('related').url()} className={classes.link} rel="noopener noreferrer" target="_blank">new account agreement</a>
              , <a href={resource.relationship('apexCustodialAgreement').link('related').url()} className={classes.link} rel="noopener noreferrer" target="_blank">custodial agreements</a>
              , <a href={resource.relationship('facetCRS').link('related').url()} className={classes.link} rel="noopener noreferrer" target="_blank">customer relationship summary</a>
              {resource.relationship('apexFdicDisclosures') ? (
                <>
                    , <a href={resource.relationship('apexFdicDisclosures').link('related').url()} className={classes.link} rel="noopener noreferrer" target="_blank">Apex FDIC-insured sweep program</a>
                </>
              ) : null}
              , and <a href={resource.relationship('apexMiscFees').link('related').url()} className={classes.link} rel="noopener noreferrer" target="_blank">miscellaneous fees</a>.
            </Text>
          }
        />
        {
          isEsg ? (
            <FormControlLabel
              className={classes.esgLabel}
              control={
                <Checkbox
                  className={classes.checkbox}
                  checked={resource.attr('esgSignature') === 'true'}
                  onChange={(e) => handleChange('esgSignature', e.target.checked.toString())}
                  name="esgSignature"
                />
            }
              label={
                <Text className={classes.textAlignLeft} color="primaryGray" variant="h5" weight="medium">
                  I, {clientNames}, acknowledge that I have received, reviewed, and agree to Facet&#39;s ESG disclosures.
                </Text>
              }
            />
          ) : undefined
        }
      </Box>
    </PageContentContainer>
  );
};

Content.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Content);
